import { useEffect, useState } from 'react'
import AnimationLayout from './components/AnimationLayout'

interface Badge {
  name: string
  emoji: string
  description: string
  points_awarded: number
}

interface Props {
  animation: { id: number; name: string; description: string }
  badge: Badge
  all_animations: { id: number; name: string }[]
}

export default function Animation39({ animation, badge, all_animations }: Props) {
  const [portalPhase, setPortalPhase] = useState(0)
  const [showBadge, setShowBadge] = useState(false)

  useEffect(() => {
    setTimeout(() => setPortalPhase(1), 200)
    setTimeout(() => setPortalPhase(2), 800)
    setTimeout(() => setPortalPhase(3), 1400)
    setTimeout(() => {
      setPortalPhase(4)
      setShowBadge(true)
    }, 2000)
  }, [])

  return (
    <AnimationLayout animation={animation} allAnimations={all_animations}>
      <style>{`
        @keyframes portalOpen {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: scale(1) rotate(180deg);
            opacity: 1;
          }
        }
        @keyframes portalSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes emergeFromPortal {
          0% {
            transform: scale(0) rotateY(180deg);
            opacity: 0;
          }
          50% {
            transform: scale(1.2) rotateY(90deg);
          }
          100% {
            transform: scale(1) rotateY(0deg);
            opacity: 1;
          }
        }
        @keyframes portalRing {
          0% {
            transform: scale(0.8);
            opacity: 0.8;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.4;
          }
          100% {
            transform: scale(0.8);
            opacity: 0.8;
          }
        }
        .portal-open {
          animation: portalOpen 0.8s ease-out forwards;
        }
        .portal-spin {
          animation: portalSpin 3s linear infinite;
        }
        .emerge {
          animation: emergeFromPortal 0.8s ease-out forwards;
        }
        .portal-ring {
          animation: portalRing 1.5s ease-in-out infinite;
        }
      `}</style>

      <div className="flex flex-col items-center justify-center min-h-[50vh] sm:min-h-[60vh]">
        <div className="relative w-56 h-56">
          {/* Anneaux du portail */}
          {portalPhase >= 1 && portalPhase < 4 && (
            <>
              <div
                className="absolute inset-0 rounded-full border-4 border-tessan-yellow portal-open portal-spin"
                style={{ animationDelay: '0s' }}
              />
              <div
                className="absolute inset-4 rounded-full border-4 border-tessan-yellow/70 portal-open portal-spin"
                style={{ animationDelay: '0.1s', animationDirection: 'reverse' }}
              />
              <div
                className="absolute inset-8 rounded-full border-4 border-tessan-yellow/50 portal-open portal-spin"
                style={{ animationDelay: '0.2s' }}
              />
            </>
          )}

          {/* Centre du portail */}
          {portalPhase >= 2 && portalPhase < 4 && (
            <div className="absolute inset-12 rounded-full bg-gradient-radial from-tessan-yellow/30 via-tessan-green-hover to-tessan-green portal-ring">
              <div className="absolute inset-0 rounded-full bg-tessan-cream/10 backdrop-blur-sm" />
            </div>
          )}

          {/* Badge émergeant */}
          {showBadge && (
            <div className="absolute inset-0 flex items-center justify-center emerge">
              <div className="w-32 h-32 sm:w-44 sm:h-44 bg-tessan-cream rounded-2xl flex items-center justify-center shadow-2xl shadow-tessan-yellow/50">
                <span className="text-6xl sm:text-8xl">{badge.emoji}</span>
              </div>
            </div>
          )}

          {/* Particules autour du portail */}
          {portalPhase >= 1 && portalPhase < 4 && Array.from({ length: 8 }, (_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full bg-tessan-yellow animate-ping"
              style={{
                left: `${50 + 45 * Math.cos((i * 45 * Math.PI) / 180)}%`,
                top: `${50 + 45 * Math.sin((i * 45 * Math.PI) / 180)}%`,
                animationDelay: `${i * 0.1}s`
              }}
            />
          ))}
        </div>

        <div className={`mt-8 text-center transition-all duration-700 ${showBadge ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="text-2xl sm:text-4xl font-black text-tessan-yellow">{badge.name}</div>
          <div className="text-lg sm:text-xl text-tessan-cream mt-2">+{badge.points_awarded} points</div>
          <div className="text-tessan-cream/70 mt-4 max-w-[280px] sm:max-w-xs">{badge.description}</div>
        </div>
      </div>
    </AnimationLayout>
  )
}
